import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useAPI } from '@hooks/useAPI'
import { getProducts, getCategories } from '@services/api/products'
import { getFullAttachmentUrl } from '@services/api/index.js'
import CategoryNav from '@components/business/CategoryNav'
import SubCategoryNav from '@components/business/SubCategoryNav'
import Card from '@components/ui/Card'
import Button from '@components/ui/Button'
import Image from '@components/ui/Image'
import Pagination from '@components/ui/Pagination'
import Loading from '@components/ui/Loading'

const Products = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // 获取URL参数
  const currentCategory = searchParams.get('category') || 'all'
  const currentSubCategory = searchParams.get('subCategory') || ''
  const currentPage = parseInt(searchParams.get('page')) || 1

  // 状态管理
  const [filters, setFilters] = useState({
    category: currentCategory,
    subCategory: currentSubCategory,
    page: currentPage
  })

  // API调用
  const { data: categoriesData, loading: categoriesLoading } = useAPI(getCategories, [i18n.language])
  const { 
    data: productsData, 
    loading: productsLoading, 
    error: productsError,
    refetch: refetchProducts 
  } = useAPI(() => {
    const params = { page: filters.page, pageSize: 8 }
    // 只有选中具体大类时，才传递分类参数
    if (filters.category !== 'all' && filters.category !== '') {
      // 关键优化：如果选中了子类，只传subTypeId；否则传typeId（大类）
      if (filters.subCategory && filters.subCategory !== '') {
        params.typeId = filters.subCategory // 仅子类ID
      } else {
        params.typeId = filters.category // 仅大类ID（无子类时）
      }
    }
    return getProducts(params)
  }, [filters, i18n.language])

  // 更新URL参数
  const updateURLParams = (newFilters) => {
    const params = new URLSearchParams()
    if (newFilters.category && newFilters.category !== 'all') {
      params.set('category', newFilters.category)
    }
    if (newFilters.subCategory) {
      params.set('subCategory', newFilters.subCategory)
    }
    if (newFilters.page && newFilters.page > 1) {
      params.set('page', newFilters.page)
    }
    setSearchParams(params)
  }

  // 处理分类切换
  const handleCategoryChange = (categoryId) => {
    const newFilters = {
      category: categoryId,
      subCategory: '', // 切换大类，清空子分类
      page: 1 // 重置页码到第一页
    }
    setFilters(newFilters)
    updateURLParams(newFilters)
  }

  // 处理子分类切换
  const handleSubCategoryChange = (subCategoryId) => {
    const newFilters = {
      ...filters,
      subCategory: subCategoryId,
      page: 1 // 重置页码
    }
    setFilters(newFilters)
    updateURLParams(newFilters)
  }

  // 处理分页
  const handlePageChange = (page) => {
    const newFilters = {
      ...filters,
      page
    }
    setFilters(newFilters)
    updateURLParams(newFilters)
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // 处理产品卡片点击
  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`)
  }

  // 获取当前分类的子分类
  const getCurrentSubCategories = (categoryId = filters.category) => {
    if (!categoriesData || filters.category === 'all') return []
     return categoriesData
      .filter(cat => {
        // 确保 parentId 和 typeId 类型一致（都转成字符串），避免类型不匹配导致过滤失败
        return String(cat.parentId) === String(filters.category)
      })
      .map(cat => ({
        id: cat.typeId, // 子类ID
        name: cat.typeName, // 子类名称（使用typeName字段）
        icon: cat.icon || '📖' // 子类图标（默认图书图标）
      }))
  }

  // 获取当前分类名称
  const getCurrentCategoryName = () => {
    if (!categoriesData) return ''
    if (filters.category === 'all') return t('products.categories.all')
    
    const category = categoriesData.find(cat => cat.typeId === filters.category)
    if (!category) return ''
    
    // 直接使用typeName字段作为分类名称
    return category.typeName
  }

  // 组件挂载时同步URL参数（只执行一次）
  useEffect(() => {
    const syncInitialState = () => {
      const urlCategory = searchParams.get('category') || 'all'
      const urlSubCategory = searchParams.get('subCategory') || ''
      const urlPage = parseInt(searchParams.get('page')) || 1
      
      if (
        urlCategory !== filters.category ||
        urlSubCategory !== filters.subCategory ||
        urlPage !== filters.page
      ) {
        setFilters({
          category: urlCategory,
          subCategory: urlSubCategory,
          page: urlPage
        })
      }
    }
    syncInitialState()
  }, []) // 空依赖，仅挂载时执行

  // 分类导航骨架屏
  const CategorySkeleton = () => (
    <div className="animate-pulse mb-8">
      <div className="flex gap-2 justify-center flex-wrap">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-10 w-24 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
    </div>
  )

  // 产品卡片骨架屏
  const ProductCardSkeleton = () => (
    <Card className="h-full min-h-[380px] border border-gray-200 bg-gray-100 flex flex-col animate-pulse">
      <div className="p-3 flex-grow flex flex-col">
        <div className="flex-1 h-[220px] bg-gray-200 rounded-md"></div>
        <div className="text-center mt-3 mb-2">
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
        </div>
      </div>
      <div className="p-3 pt-0">
        <div className="h-10 bg-gray-200 rounded-md w-full"></div>
      </div>
    </Card>
  )

  return (
      <div className="products-page">
        {/* 页面标题区 */}
        <section className="hero-banner bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
          <div className="container section-padding text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('products.title')}
            </h1>
            <p className="text-lg md:text-xl text-primary-100 max-w-3xl mx-auto">
              {t('products.subtitle')}
            </p>
          </div>
        </section>

        <div className="container section-padding pt-12">
          {/* 大类分类导航 */}
          {categoriesLoading ? (
            <CategorySkeleton />
          ) : categoriesData ? (
            <CategoryNav
              categories={categoriesData.filter(cat => !cat.parentId).map(cat => ({
                id: cat.typeId,
                name: cat.typeName,
                icon: cat.icon || '📚'
              }))}
              activeCategory={filters.category}
              onCategoryChange={handleCategoryChange}
              className="mb-8"
            />
          ) : null}

          {/* 子分类导航：仅选中具体大类时显示 */}
          {filters.category !== 'all' && !categoriesLoading && categoriesData && (
            <SubCategoryNav
              subCategories={getCurrentSubCategories()}
              activeSubCategory={filters.subCategory}
              onSubCategoryChange={handleSubCategoryChange}
              currentCategoryName={getCurrentCategoryName()}
              className="mb-8"
            />
          )}

          {/* 产品列表区 */}
          <div className="products-section">
            {productsLoading ? (
              // 加载中显示骨架屏
              <div className="products-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {[...Array(8)].map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            ) : productsError ? (
              <div className="text-center py-12">
                <div className="text-red-600 mb-4">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-medium">{t('products.states.loadFailed')}</p>
                  <p className="text-sm text-gray-500 mt-2">{productsError}</p>
                </div>
                <button
                  onClick={() => refetchProducts()}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {t('products.states.retry')}
                </button>
              </div>
            ) : productsData?.records?.length > 0 ? (
              <>
                {/* 产品网格 */}
                <div className="products-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                  {productsData.records.map((product) => (
                    <Card key={product.productId} className="h-full min-h-[380px] border border-gray-200 bg-gray-100 shadow-lg transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col">
                      <div className="p-3 flex-grow flex flex-col">
                        <div className="flex-1 h-[220px] bg-white rounded-md flex items-center justify-center overflow-hidden">
                          <div className="w-full h-full flex items-center justify-center">
                            {product?.coverImage ? (
                              <Image
                                src={getFullAttachmentUrl(product?.coverImage)}
                                alt={product.productName}
                                className="max-h-full max-w-full"
                                imgClassName="max-h-full max-w-full object-contain"
                              />
                            ) : (
                              <img
                                src="/images/placeholder-book.png"
                                alt={product.productName}
                                className="max-h-full max-w-full object-contain"
                              />
                            )}
                          </div>
                        </div>
                        
                        <div className="text-center mt-3 mb-2">
                          <p className="text-sm text-gray-900 font-medium line-clamp-2">
                            {product.productName}
                          </p>
                        </div>
                      </div>
                      
                      <div className="p-3 pt-0">
                        <Button 
                          as={Link}
                          to={`/products/${product.productId}`}
                          variant="secondary" 
                          size="sm" 
                          className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-2 rounded-md w-full"
                        >
                          {t('common.viewDetail', '查看详情')}
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* 分页组件 */}
                <div className="flex justify-center mt-8">
                  <Pagination
                    currentPage={productsData.pagination?.currentPage || productsData.current || filters.page}
                    totalPages={productsData.pagination?.totalPages || productsData.pages || Math.ceil((productsData.total || productsData.pagination?.totalItems || 0) / 12) || 1}
                    totalItems={productsData.total || productsData.pagination?.totalItems || 0}
                    itemsPerPage={productsData.pagination?.itemsPerPage || productsData.size || 8}
                    onPageChange={handlePageChange}
                    showQuickJump={true}
                    showBackToTop={true}
                  />
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="text-lg font-medium text-gray-600">{t('products.states.noProducts')}</p>
                  <p className="text-sm text-gray-500 mt-2">{t('products.states.noProductsHint')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  )
}

export default Products